import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Zap, TrendingUp, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function UsageBar({ refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [reviewMessage, setReviewMessage] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/usage");
      setData(data); setReviewMessage("");
    } catch (err) {
      if (err?.response?.status === 409) {
        const detail = err.response.data?.detail;
        setData(null);
        setReviewMessage(typeof detail === "string" ? detail : detail?.message || "Your existing entitlement needs billing review.");
      }
      if (process.env.NODE_ENV !== "production") console.warn("Load usage failed:", err?.message);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 p-4 flex items-center gap-3 text-xs font-mono text-zinc-500" data-testid="usage-bar-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading usage...
      </div>
    );
  }
  if (reviewMessage) return <div className="border border-amber-500/30 bg-amber-500/5 p-4 mb-6 text-sm text-amber-200" role="status" data-testid="billing-entitlement-review"><strong>Balance preserved · review required</strong><p className="text-xs mt-2 text-zinc-400">{reviewMessage} No free allowance has been substituted.</p><Link to="/support?tab=help" data-testid="billing-review-help" className="text-xs underline inline-block mt-2">Contact billing support</Link></div>;
  if (!data) return null;

  // Admin bypass — dedicated compact bar, no quota UI.
  if (data.is_admin) {
    return (
      <div
        data-testid="usage-bar-admin"
        className="border border-emerald-500/30 bg-emerald-500/5 p-4 md:p-5 flex items-center gap-3"
      >
        <Zap className="w-4 h-4 flex-shrink-0 text-emerald-400" />
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
            Admin
          </span>
          <span className="text-xs text-white">Unlimited transcription · no quota</span>
        </div>
      </div>
    );
  }

  const percent = Math.min(100, Math.round(((data.minutes_used + (data.minutes_adjusted || 0)) / Math.max(1, data.minutes_quota)) * 100));
  const isLow = data.minutes_remaining < data.minutes_quota * 0.1;
  const isEmpty = data.minutes_remaining <= 0;

  return (
    <div
      data-testid="usage-bar"
      className={`border p-4 md:p-5 flex flex-col md:flex-row md:items-center gap-4 ${isEmpty ? "border-red-500/30 bg-red-500/5" : isLow ? "border-amber-400/30 bg-amber-400/5" : "border-zinc-800 bg-zinc-900/40"}`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <Zap className={`w-4 h-4 flex-shrink-0 ${isEmpty ? "text-red-400" : isLow ? "text-amber-400" : "text-blue-400"}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {data.plan.name}
            </span>
            <span className="text-xs text-white" data-testid="usage-numbers">
              {data.plan.key === "free"
                ? `${data.free_is_legacy ? "Legacy free balance" : "Free trial"}: ${data.free_minutes_remaining.toFixed(1)} / ${data.free_minutes_granted} minutes remaining`
                : `${data.minutes_remaining.toFixed(1)} minutes remaining · ${data.minutes_used.toFixed(1)} used${data.minutes_adjusted > 0 ? ` · ${data.minutes_adjusted.toFixed(1)} adjusted` : ""}`}
            </span>
            <span className="text-[10px] font-mono text-zinc-600 ml-auto" data-testid="usage-validity">
              {data.is_paid && (data.next_expiry || data.period_end)
                ? `Next expiry ${new Date(data.next_expiry || data.period_end).toLocaleDateString()}`
                : "One time · no renewal"}
            </span>
          </div>
          <div className="h-1 bg-zinc-800 relative overflow-hidden">
            <div
              data-testid="usage-progress"
              className={`h-full transition-all duration-500 ${isEmpty ? "bg-red-400" : isLow ? "bg-amber-400" : "bg-blue-500"}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>
      <Link
        to="/pricing"
        data-testid="usage-upgrade-link"
        className={`flex-shrink-0 h-9 px-4 flex items-center gap-2 text-xs font-semibold font-mono uppercase tracking-widest transition-colors duration-200 ${isLow || isEmpty ? "bg-white text-zinc-950 hover:bg-zinc-200" : "border border-zinc-800 text-zinc-300 hover:bg-zinc-800 hover:text-white"}`}
      >
        <TrendingUp className="w-3 h-3" /> {isEmpty ? "Upgrade" : data.plan.key === "agency" ? "Manage" : "Upgrade"}
      </Link>
    </div>
  );
}
